import React from "react";
import { FaTimes } from "react-icons/fa";

const fieldClass =
  "w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-brand-cream text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold-ink";
const labelClass = "block text-sm font-medium text-brand-charcoal mb-1";
const menuClass =
  "absolute z-10 w-full mt-1 bg-brand-cream border border-brand-charcoal/20 rounded-lg max-h-60 overflow-auto";
const optionClass =
  "w-full px-4 py-2 text-left text-sm text-brand-charcoal hover:bg-brand-gold/10 focus:bg-brand-gold/10 focus:outline-none";

const AdvancedFilters = ({
  selectedYear,
  setSelectedYear,
  selectedOrganization,
  setSelectedOrganization,
  selectedCenter,
  setSelectedCenter,
  selectedStatus,
  setSelectedStatus,
  selectedEvent,
  setSelectedEvent,
  dateRange,
  setDateRange,
  years,
  statusOptions,
  eventOptions,
  organizations,
  bowlingCenters,
  orgSearchTerm,
  setOrgSearchTerm,
  centerSearchTerm,
  setCenterSearchTerm,
  showOrgResults,
  setShowOrgResults,
  showCenterResults,
  setShowCenterResults,
  showOrganizationFilter = true,
}) => {
  return (
    <div className="border border-brand-charcoal/15 bg-brand-cream p-4">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="year-filter" className={labelClass}>
              Year
            </label>
            <select
              id="year-filter"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={fieldClass}
              aria-label="Filter by year"
            >
              <option value="">All years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status-filter" className={labelClass}>
              Status
            </label>
            <select
              id="status-filter"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={fieldClass}
              aria-label="Filter by status"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="event-filter" className={labelClass}>
              Event type
            </label>
            <select
              id="event-filter"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className={fieldClass}
              aria-label="Filter by event type"
            >
              {eventOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="center-search" className={labelClass}>
              Bowling center
            </label>
            <div className="relative">
              <input
                id="center-search"
                type="text"
                placeholder="Search bowling centers"
                value={centerSearchTerm}
                onChange={(e) => {
                  setCenterSearchTerm(e.target.value);
                  setSelectedCenter(null);
                  setShowCenterResults(true);
                }}
                onFocus={() => setShowCenterResults(true)}
                className={fieldClass}
                aria-label="Search and filter by bowling center"
                aria-autocomplete="list"
                aria-expanded={showCenterResults}
                aria-controls="center-results"
              />
              {selectedCenter && (
                <button
                  onClick={() => {
                    setSelectedCenter(null);
                    setCenterSearchTerm("");
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-brand-muted hover:text-brand-charcoal focus:outline-none"
                  aria-label="Clear selected bowling center"
                >
                  <FaTimes className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              {showCenterResults && bowlingCenters.length > 0 && (
                <div
                  id="center-results"
                  className={menuClass}
                  role="listbox"
                  aria-label="Available bowling centers"
                >
                  {bowlingCenters.map((center) => (
                    <button
                      key={center.id}
                      onClick={() => {
                        setSelectedCenter(center);
                        setCenterSearchTerm(center.centerName);
                        setShowCenterResults(false);
                      }}
                      className={optionClass}
                      role="option"
                      aria-selected={selectedCenter?.id === center.id}
                    >
                      {center.centerName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="start-date" className={labelClass}>
              From
            </label>
            <input
              id="start-date"
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
              className={fieldClass}
              aria-label="Filter by start date"
            />
          </div>
          <div>
            <label htmlFor="end-date" className={labelClass}>
              To
            </label>
            <input
              id="end-date"
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
              className={fieldClass}
              aria-label="Filter by end date"
            />
          </div>
        </div>

        {showOrganizationFilter && (
          <div>
            <label htmlFor="org-search" className={labelClass}>
              Organization
            </label>
            <div className="relative">
              <input
                id="org-search"
                type="text"
                placeholder="Search organizations"
                value={orgSearchTerm}
                onChange={(e) => {
                  setOrgSearchTerm(e.target.value);
                  setSelectedOrganization(null);
                  setShowOrgResults(true);
                }}
                onFocus={() => setShowOrgResults(true)}
                className={fieldClass}
                aria-label="Search and filter by organization"
                aria-autocomplete="list"
                aria-expanded={showOrgResults}
                aria-controls="org-results"
              />
              {selectedOrganization && (
                <button
                  onClick={() => {
                    setSelectedOrganization(null);
                    setOrgSearchTerm("");
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-brand-muted hover:text-brand-charcoal focus:outline-none"
                  aria-label="Clear selected organization"
                >
                  <FaTimes className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              {showOrgResults && organizations.length > 0 && (
                <div
                  id="org-results"
                  className={menuClass}
                  role="listbox"
                  aria-label="Available organizations"
                >
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => {
                        setSelectedOrganization(org);
                        setOrgSearchTerm(org.name);
                        setShowOrgResults(false);
                      }}
                      className={optionClass}
                      role="option"
                      aria-selected={selectedOrganization?.id === org.id}
                    >
                      {org.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedFilters;
